import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Routes, Route, useNavigate } from "react-router-dom";
import { StatCard, Empty, RolePill, TicketStatus, TICKET_LABEL } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Building2, DollarSign, Users, Layers, ShieldCheck, LogIn, Power, Loader2, Plus,
  MoreVertical, History, Pencil, Ban, XCircle, Trash2, Send, KeyRound, GitPullRequest,
  Package, CheckCircle2, Archive, RefreshCw, Search, HardDrive, AlertTriangle, Database,
  ChevronRight, ChevronDown, Download, LifeBuoy,
} from "lucide-react";
import { Paperclip } from "lucide-react";
import { uploadTicketFiles, AttachmentChips } from "@/pages/m1/SupportHelp";

const MOD_LABEL = { module_1: "Data Collection" };
const MOD_COLOR = { module_1: "#4F46E5" };

export default function FounderConsole() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="tenants" element={<Tenants />} />
      <Route path="users" element={<UsersTab />} />
      <Route path="plans" element={<Plans />} />
      <Route path="requests" element={<PlanRequests />} />
      <Route path="support" element={<SupportAccess />} />
      <Route path="support-tickets" element={<SupportTickets />} />
      <Route path="audit" element={<AuditLog />} />
      <Route path="storage" element={<StorageHealth />} />
    </Routes>
  );
}

function Header({ title, sub, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="overline text-azx-founder">Platform Console</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1>
        {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
      </div>
      {action}
    </div>
  );
}
function Loading() { return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>; }
function StatusChip({ status }) {
  const m = { active: "bg-emerald-100 text-emerald-700", suspended: "bg-amber-100 text-amber-700", inactive: "bg-slate-200 text-slate-600" };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${m[status] || m.inactive}`}>{status}</span>;
}

/* ---------------- Support Tickets ---------------- */
function SupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    setLoading(true);
    const qs = status === "all" ? "" : `?status=${status}`;
    api.get(`/platform/support-tickets${qs}`).then((r) => setTickets(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <Header title="Support Tickets" sub="Tickets raised by firms. Reply and move each through its lifecycle." />
      <div className="mb-4 flex gap-2 flex-wrap" data-testid="ticket-filters">
        {["all", "received", "in_progress", "completed", "closed"].map((s) => (
          <button key={s} data-testid={`ticket-filter-${s}`} onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${status === s ? "bg-azx-founder text-white border-azx-founder" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300"}`}>
            {s === "all" ? "All" : TICKET_LABEL[s]}</button>
        ))}
      </div>
      {loading ? <Loading /> : tickets.length === 0 ? <Empty icon={LifeBuoy} title="No tickets" /> : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="platform-tickets-table">
            <thead className="bg-slate-50 dark:bg-slate-900 text-left text-[11px] uppercase tracking-wide text-slate-400">
              <tr><th className="p-3">Subject</th><th className="p-3">Firm</th><th className="p-3">Raised by</th><th className="p-3">Status</th><th className="p-3">Updated</th></tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} data-testid={`platform-ticket-${t.id}`} onClick={() => setOpen(t.id)}
                  className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50">
                  <td className="p-3 font-medium">{t.subject}</td>
                  <td className="p-3">{t.tenant_name}</td>
                  <td className="p-3">{t.raised_by_name}</td>
                  <td className="p-3"><TicketStatus status={t.status} /></td>
                  <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{new Date(t.updated_at || t.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <FounderTicketDialog ticketId={open} onClose={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function FounderTicketDialog({ ticketId, onClose }) {
  const [t, setT] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/platform/support-tickets/${ticketId}`).then((r) => setT(r.data)).catch(() => {}), [ticketId]);
  useEffect(() => { load(); }, [load]);
  const changeStatus = async (s) => {
    try { await api.post(`/platform/support-tickets/${ticketId}/status`, { status: s }); toast.success(`Status set to ${TICKET_LABEL[s]}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const send = async () => {
    if (!reply.trim() && files.length === 0) return;
    setBusy(true);
    try {
      const attachments = await uploadTicketFiles(files, "/platform/support-tickets/upload-url", { tid: ticketId });
      await api.post(`/platform/support-tickets/${ticketId}/reply`, { body: reply, internal, attachments });
      setReply(""); setInternal(false); setFiles([]); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="founder-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6">{t?.subject || "Ticket"}</DialogTitle>
          <DialogDescription>{t ? `${t.tenant_name} · ${t.raised_by_name}` : ""}</DialogDescription>
        </DialogHeader>
        {!t ? <Loading /> : (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Label className="text-xs">Status</Label>
              <Select value={t.status} onValueChange={changeStatus}>
                <SelectTrigger data-testid="founder-ticket-status" className="w-44 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-900 p-3 text-sm">
              <div className="text-[11px] text-slate-400 mb-1">{t.raised_by_name} · {new Date(t.created_at).toLocaleString()}</div>
              <p className="whitespace-pre-wrap">{t.body}</p>
              <AttachmentChips ticketId={ticketId} atts={t.attachments} base="/platform/support-tickets" />
            </div>
            <div className="mt-3 space-y-3 max-h-[36vh] overflow-y-auto" data-testid="founder-ticket-messages">
              {(t.messages || []).map((m) => (
                <div key={m.id} className={`rounded-lg p-3 text-sm ${m.internal ? "bg-amber-50 dark:bg-amber-950/30 border border-dashed border-amber-300 dark:border-amber-800" : m.author_side === "founder" ? "bg-indigo-50 dark:bg-indigo-950/40" : "bg-slate-50 dark:bg-slate-900"}`}>
                  <div className="text-[11px] text-slate-400 mb-1">{m.author_name}{m.internal ? " · internal note" : ""} · {new Date(m.created_at).toLocaleString()}</div>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <AttachmentChips ticketId={ticketId} atts={m.attachments} base="/platform/support-tickets" />
                </div>
              ))}
              {(t.messages || []).length === 0 && <p className="text-sm text-slate-400 text-center py-3">No replies yet.</p>}
            </div>
            <div className="mt-3">
              <Textarea data-testid="founder-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply to the firm…" className="min-h-[70px]" />
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer"><Checkbox data-testid="founder-reply-internal" checked={internal} onCheckedChange={(v) => setInternal(!!v)} /> Internal note (hidden from the firm)</label>
                <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer"><Paperclip className="h-3.5 w-3.5" /><input type="file" multiple data-testid="founder-reply-file" className="text-xs" onChange={(e) => setFiles(e.target.files)} /></label>
                <Button data-testid="founder-reply-send" onClick={send} disabled={busy} className="bg-azx-founder hover:opacity-90">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />}Send</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/platform/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);
  if (!m) return <Loading />;
  return (
    <div>
      <Header title="Platform Overview" sub="Cross-tenant operational metadata only — no tenant business data." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Tenants" value={`${m.active_count}/${m.tenant_count}`} icon={Building2} accent="#D97706" sub="active / total" />
        <StatCard label="Monthly Recurring" value={`$${m.total_mrr.toLocaleString()}`} icon={DollarSign} accent="#059669" />
        <StatCard label="Total Users" value={m.total_users} icon={Users} accent="#2563EB" />
        <StatCard label="Pending Requests" value={m.pending_requests} icon={GitPullRequest} accent="#8B5CF6" />
      </div>
      <div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 p-5">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-900 dark:text-amber-200">Founder access boundary</div>
            <p className="text-sm text-amber-800/80 dark:text-amber-300/70 mt-1">You cannot read a tenant's business data by default. Use <b>Support Access</b> for an explicit, logged, temporary elevation.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Tenants ---------------- */
function Tenants() {
  const { loadMe } = useAuth();
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {type, tenant}
  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/platform/tenants"), api.get("/platform/plans")])
      .then(([t, p]) => { setTenants(t.data); setPlans(p.data.filter((x) => x.status === "active")); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (t, action) => {
    try { await api.post(`/platform/tenants/${t.id}/status`, { action }); toast.success(`Tenant ${action}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const biz = tenants.filter((t) => !t.is_platform_tenant);
  const activeCount = biz.filter((t) => t.status === "active").length;

  return (
    <div>
      <Header title="Tenants" sub={`${biz.length} tenants · ${activeCount} active`}
        action={<Button data-testid="create-tenant-btn" className="bg-azx-founder hover:bg-amber-700" onClick={() => setModal({ type: "create" })}><Plus className="mr-2 h-4 w-4" /> Create tenant</Button>} />
      {loading ? <Loading /> : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Firm</th><th className="px-4 py-3 text-left">Primary Contact</th>
                <th className="px-4 py-3 text-left">Collection Plan</th>
                <th className="px-4 py-3 text-left">Users</th><th className="px-4 py-3 text-left">Engagements</th>
                <th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium">
                      <span className="flex h-7 w-7 items-center justify-center rounded text-white text-[10px] font-bold" style={{ backgroundColor: t.branding?.primary || "#475569" }}>{t.branding?.logo_text || t.name?.[0]}</span>
                      {t.name}
                      {t.is_platform_tenant && <span className="rounded bg-azx-founder/15 text-azx-founder px-1.5 py-0.5 text-[10px] font-bold">PLATFORM</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><div className="text-slate-700 dark:text-slate-300">{t.primary_contact_name || "—"}</div><div className="text-[11px] text-slate-400 font-mono">{t.primary_contact_email}</div></td>
                  <td className="px-4 py-3">{t.collection_plan || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3">{t.user_count}</td>
                  <td className="px-4 py-3">{t.engagement_count ?? <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3"><StatusChip status={t.status} /></td>
                  <td className="px-4 py-3 text-right">
                    {!t.is_platform_tenant && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`tenant-actions-${t.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem data-testid={`history-${t.id}`} onClick={() => setModal({ type: "history", tenant: t })}><History className="mr-2 h-4 w-4" /> View history</DropdownMenuItem>
                          <DropdownMenuItem data-testid={`edit-${t.id}`} onClick={() => setModal({ type: "edit", tenant: t })}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {t.status !== "suspended"
                            ? <DropdownMenuItem data-testid={`suspend-${t.id}`} onClick={() => setStatus(t, "suspend")}><Ban className="mr-2 h-4 w-4" /> Suspend</DropdownMenuItem>
                            : <DropdownMenuItem data-testid={`activate-${t.id}`} onClick={() => setStatus(t, "activate")}><Power className="mr-2 h-4 w-4" /> Reactivate</DropdownMenuItem>}
                          <DropdownMenuItem data-testid={`cancel-${t.id}`} onClick={() => setStatus(t, "cancel")}><XCircle className="mr-2 h-4 w-4" /> Cancel / Deactivate</DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" data-testid={`delete-${t.id}`} onClick={() => setModal({ type: "delete", tenant: t })}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal?.type === "create" && <TenantModal plans={plans} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "edit" && <TenantModal plans={plans} tenant={modal.tenant} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "delete" && <DeleteTenant tenant={modal.tenant} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "history" && <HistoryModal tenant={modal.tenant} onClose={() => setModal(null)} />}
    </div>
  );
}

function TenantModal({ plans, tenant, onClose, onDone }) {
  const editing = !!tenant;
  const [f, setF] = useState({
    name: tenant?.name || "", primary_contact_name: tenant?.primary_contact_name || "",
    primary_contact_email: tenant?.primary_contact_email || "", region: tenant?.region || "us-east",
    seat_limit: tenant?.seat_limit ?? "", engagement_limit: tenant?.engagement_limit ?? "",
    pl_preset: "ias1",
    admin_name: "", admin_email: "",
  });
  const subInit = (mod) => tenant?.subscriptions?.[mod] || { status: "none", plan_id: null };
  const [m1, setM1] = useState(subInit("module_1"));
  const save = async () => {
    if (!f.name) return toast.error("Firm name required");
    const subscriptions = { module_1: m1 };
    const payload = {
      ...f, subscriptions,
      seat_limit: f.seat_limit === "" ? null : Number(f.seat_limit),
      engagement_limit: f.engagement_limit === "" ? null : Number(f.engagement_limit),
    };
    if (editing) delete payload.pl_preset;
    try {
      if (editing) { await api.patch(`/platform/tenants/${tenant.id}`, payload); toast.success("Tenant updated"); }
      else {
        const { data: res } = await api.post("/platform/tenants", payload);
        if (res?.invited && res?.email_sent === false)
          toast.warning("Tenant created, but the invite email could not be sent — check the admin email address.");
        else if (res?.invited)
          toast.success("Tenant created — admin invite emailed");
        else
          toast.success("Tenant created");
      }
      onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  const ModRow = ({ mod, val, setVal }) => {
    const modPlans = plans.filter((p) => p.module === mod);
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold" style={{ color: MOD_COLOR[mod] }}>{MOD_LABEL[mod]}</span>
          <Select value={val.status} onValueChange={(v) => setVal({ ...val, status: v })}>
            <SelectTrigger className="h-7 w-24 text-xs" data-testid={`tenant-${mod}-status`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="trial">Trial</SelectItem><SelectItem value="none">None</SelectItem></SelectContent>
          </Select>
        </div>
        <Select value={val.plan_id || ""} onValueChange={(v) => setVal({ ...val, plan_id: v })} disabled={val.status === "none"}>
          <SelectTrigger className="h-8 text-xs" data-testid={`tenant-${mod}-plan`}><SelectValue placeholder="Select plan" /></SelectTrigger>
          <SelectContent>{modPlans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · ${p.priceMonthly}/mo</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit tenant" : "Create tenant"}</DialogTitle><DialogDescription>Firm, contact, region and module subscriptions.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Firm / organization name</Label><Input data-testid="tenant-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact name</Label><Input data-testid="tenant-contact-name" className="mt-1" value={f.primary_contact_name} onChange={(e) => setF({ ...f, primary_contact_name: e.target.value })} /></div>
            <div><Label>Contact email</Label><Input data-testid="tenant-contact-email" className="mt-1" value={f.primary_contact_email} onChange={(e) => setF({ ...f, primary_contact_email: e.target.value })} /></div>
          </div>
          <div><Label>Region (data residency)</Label>
            <Select value={f.region} onValueChange={(v) => setF({ ...f, region: v })}>
              <SelectTrigger className="mt-1" data-testid="tenant-region"><SelectValue /></SelectTrigger>
              <SelectContent>{["us-east", "eu-west", "ap-south", "global"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><ModRow mod="module_1" val={m1} setVal={setM1} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>User limit</Label><Input type="number" min="0" data-testid="tenant-seat-limit" className="mt-1" value={f.seat_limit} onChange={(e) => setF({ ...f, seat_limit: e.target.value })} placeholder="Plan default" /></div>
            <div><Label>Engagement limit</Label><Input type="number" min="0" data-testid="tenant-engagement-limit" className="mt-1" value={f.engagement_limit} onChange={(e) => setF({ ...f, engagement_limit: e.target.value })} placeholder="Plan default" /></div>
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Leave blank to use the plan's caps. Set a number to override for this tenant. Client users never count toward the user limit.</p>
          {!editing && (
            <div><Label>Default IFRS reporting framework</Label>
              <Select value={f.pl_preset} onValueChange={(v) => setF({ ...f, pl_preset: v })}>
                <SelectTrigger className="mt-1" data-testid="tenant-pl-preset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ias1">IAS 1 (current standard)</SelectItem>
                  <SelectItem value="ifrs18">IFRS 18 (effective 1 Jan 2027)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Seeds this tenant's request categories. Tenant Admins can switch or add more later in Configure.</p>
            </div>
          )}
          {!editing && (
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 p-3">
              <div className="text-xs font-semibold text-slate-500 mb-2">Invite initial Tenant Admin (sends a signup link)</div>
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="tenant-admin-name" placeholder="Admin name" value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} />
                <Input data-testid="tenant-admin-email" placeholder="Admin email" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter><Button onClick={save} data-testid="tenant-save" className="bg-azx-founder hover:bg-amber-700">{editing ? "Save changes" : "Create tenant"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteTenant({ tenant, onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const del = async () => {
    try { await api.delete(`/platform/tenants/${tenant.id}?confirm_name=${encodeURIComponent(confirm)}`); toast.success("Tenant deleted (30-day grace)"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="text-red-600">Delete {tenant.name}?</DialogTitle>
          <DialogDescription>Access is cut off immediately; data is recoverable for 30 days before permanent purge. Re-type the firm name to confirm.</DialogDescription></DialogHeader>
        <Input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={tenant.name} />
        <DialogFooter><Button variant="destructive" disabled={confirm !== tenant.name} data-testid="delete-confirm-btn" onClick={del}>Delete permanently</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryModal({ tenant, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/platform/tenants/${tenant.id}/history`).then((r) => setData(r.data)); }, [tenant]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{tenant.name} — history</DialogTitle><DialogDescription>Platform actions and tenant activity.</DialogDescription></DialogHeader>
        {!data ? <Loading /> : (
          <div className="space-y-4">
            <div>
              <div className="overline text-azx-founder mb-2">Platform actions</div>
              {data.platform_actions.length === 0 ? <p className="text-sm text-slate-400">None</p> : data.platform_actions.map((a) => (
                <div key={a.id} className="text-sm py-1 border-b border-slate-100 dark:border-slate-800"><b>{a.action}</b> · {a.resource} <span className="text-[11px] text-slate-400">{new Date(a.created_at).toLocaleString()}</span></div>
              ))}
            </div>
            <div>
              <div className="overline text-indigo-500 mb-2">Tenant activity</div>
              {data.tenant_activity.length === 0 ? <p className="text-sm text-slate-400">None</p> : data.tenant_activity.slice(0, 30).map((a) => (
                <div key={a.id} className="text-sm py-1 border-b border-slate-100 dark:border-slate-800">{a.actor_name} {a.action} <span className="text-[11px] text-slate-400">{new Date(a.created_at).toLocaleString()}</span></div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Users ---------------- */
const M1_INTERNAL = ["partner", "manager", "staff"];
const CAT_LABEL = { founder: "Founder", tenant_user: "Tenant User", client: "Client" };
const TABS = [["all", "All"], ["founder", "Founder Users"], ["tenant_user", "Tenant Users"], ["client", "Tenant's Clients"]];

function UserStatus({ status }) {
  const active = status !== "inactive";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${status === "invited" ? "bg-amber-100 text-amber-700" : active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{status || "active"}</span>;
}

function UsersTab() {
  const [tab, setTab] = useState("all");
  const [data, setData] = useState({ users: [], total: 0, shown: 0, counts: {} });
  const [q, setQ] = useState("");
  const [firm, setFirm] = useState("all");
  const [tenants, setTenants] = useState([]);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (tab !== "all") params.set("category", tab);
    if (firm !== "all") params.set("firm", firm);
    api.get(`/platform/users?${params.toString()}`).then((r) => setData(r.data));
  }, [q, tab, firm]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/platform/tenants").then((r) => setTenants(r.data.filter((t) => !t.is_platform_tenant))); }, []);

  const del = async (u) => { try { await api.delete(`/platform/users/${u.id}`); toast.success("User deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reset = async (u) => { try { const { data: r } = await api.post(`/platform/users/${u.id}/send-reset`); toast.success(r.email_sent ? "Reset link emailed" : "Reset link generated (demo address not delivered)"); } catch (e) { toast.error(apiError(e)); } };
  const setActive = async (u, active) => { try { await api.post(`/platform/users/${u.id}/set-active`, { active, reason: active ? "" : "support/abuse action" }); toast.success(active ? "Reactivated" : "Deactivated (logged)"); load(); } catch (e) { toast.error(apiError(e)); } };

  const showFirmFilter = tab === "tenant_user" || tab === "client" || tab === "all";
  const c = data.counts || {};

  return (
    <div>
      <Header title="Users" sub="Platform-wide directory across Founder, tenant staff and tenant clients."
        action={<div className="flex gap-2">
          {(tab === "founder" || tab === "all") && <Button variant="outline" data-testid="add-platform-admin-btn" onClick={() => setModal({ type: "platform" })}><KeyRound className="mr-2 h-4 w-4" /> Add Platform Admin</Button>}
          {(tab === "tenant_user" || tab === "all") && <Button className="bg-azx-founder hover:bg-amber-700" data-testid="add-tenant-user-btn" onClick={() => setModal({ type: "tenant" })}><Plus className="mr-2 h-4 w-4" /> Add tenant user</Button>}
        </div>} />

      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 p-0.5 bg-slate-50 dark:bg-slate-800 mb-4">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`usertab-${k}`} onClick={() => setTab(k)}
            className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-azx-founder text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {label}{k !== "all" && c[k] != null && <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-black/10 px-1.5 text-[10px] font-bold">{c[k]}</span>}
          </button>
        ))}
      </div>

      {tab === "client" && (
        <div className="mb-3 rounded-md bg-indigo-50 dark:bg-indigo-950/30 px-3 py-2 text-xs text-indigo-700 dark:text-indigo-300">
          External client accounts are created tenant-side from an engagement's Client Team. This is read-only oversight; they do not count against a tenant's seat cap.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${tab === "all" ? "all users" : CAT_LABEL[tab] + "s"}…`} className="pl-9" />
        </div>
        {showFirmFilter && (
          <Select value={firm} onValueChange={setFirm}>
            <SelectTrigger className="w-52" data-testid="firm-filter"><SelectValue placeholder="All firms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All firms</SelectItem>
              {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="text-xs text-slate-400 mb-2">{data.shown} of {data.total} users</div>

      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {tab === "all" && <th className="px-4 py-3 text-left">Category</th>}
              <th className="px-4 py-3 text-left">Name</th><th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              {tab !== "founder" && <th className="px-4 py-3 text-left">Firm</th>}
              {tab === "client" && <th className="px-4 py-3 text-left">Engagement(s)</th>}
              <th className="px-4 py-3 text-left">Status</th>
              {tab !== "client" && <th className="px-4 py-3 text-left">Last login</th>}
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} data-testid={`platform-user-${u.id}`} className="border-t border-slate-100 dark:border-slate-800">
                {tab === "all" && <td className="px-4 py-3"><span className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-500">{CAT_LABEL[u.category]}</span></td>}
                <td className="px-4 py-3 font-medium">{u.name}</td>
                <td className="px-4 py-3 text-slate-500 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3"><RolePill role={u.display_role} /></td>
                {tab !== "founder" && <td className="px-4 py-3 text-slate-500">{u.firm}</td>}
                {tab === "client" && <td className="px-4 py-3 text-slate-500 text-xs max-w-[220px] truncate">{(u.engagements || []).join(", ") || "—"}</td>}
                <td className="px-4 py-3"><UserStatus status={u.status} /></td>
                {tab !== "client" && <td className="px-4 py-3 text-xs text-slate-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : "—"}</td>}
                <td className="px-4 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`user-actions-${u.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem data-testid={`user-reset-${u.id}`} onClick={() => reset(u)}><Send className="mr-2 h-4 w-4" /> Send password reset</DropdownMenuItem>
                      {u.category === "client" ? (
                        u.status === "inactive"
                          ? <DropdownMenuItem data-testid={`reactivate-${u.id}`} onClick={() => setActive(u, true)}><Power className="mr-2 h-4 w-4" /> Reactivate</DropdownMenuItem>
                          : <DropdownMenuItem className="text-red-600" data-testid={`deactivate-${u.id}`} onClick={() => setActive(u, false)}><Ban className="mr-2 h-4 w-4" /> Deactivate</DropdownMenuItem>
                      ) : (
                        <>
                          <DropdownMenuItem data-testid={`user-edit-${u.id}`} onClick={() => setModal({ type: "edit", user: u })}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                          {u.display_role !== "founder" && <><DropdownMenuSeparator /><DropdownMenuItem className="text-red-600" data-testid={`user-delete-${u.id}`} onClick={() => del(u)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem></>}
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
            {data.users.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No users in this category</td></tr>}
          </tbody>
        </table>
      </div>
      {modal?.type === "platform" && <PlatformAdminModal onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "tenant" && <TenantUserModal tenants={tenants} onClose={() => setModal(null)} onDone={load} />}
      {modal?.type === "edit" && <EditUserModal user={modal.user} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}

function PlatformAdminModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "" });
  const save = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    try { const { data: r } = await api.post("/platform/users/platform-admin", f); toast.success(r?.email_sent === false ? "Platform Admin created, but the invite email failed — check the address." : "Platform Admin invited"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle>Add Platform Admin</DialogTitle><DialogDescription>Creates a platform-scoped admin and emails a signup link.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="pa-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="pa-email" className="mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="pa-save">Invite</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TenantUserModal({ tenants, onClose, onDone }) {
  const [f, setF] = useState({ tenant_id: "", name: "", email: "", role: "" });
  const sel = tenants.find((t) => t.id === f.tenant_id);
  const active = sel ? Object.entries(sel.subscriptions || {}).filter(([, s]) => ["active", "trial"].includes(s.status)).map(([m]) => m) : [];
  let roles = ["tenant_admin"];
  if (active.includes("module_1")) roles = roles.concat(M1_INTERNAL);
  const save = async () => {
    if (!f.tenant_id || !f.name || !f.email || !f.role) return toast.error("All fields required");
    try { const { data: r } = await api.post("/platform/users/tenant-user", f); toast.success(r?.email_sent === false ? "User created, but the invite email failed — check the address." : "User invited"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle>Add tenant user</DialogTitle><DialogDescription>Role choices are limited to the tenant's active modules.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tenant</Label>
            <Select value={f.tenant_id} onValueChange={(v) => setF({ ...f, tenant_id: v, role: "" })}>
              <SelectTrigger className="mt-1" data-testid="tu-tenant"><SelectValue placeholder="Select tenant" /></SelectTrigger>
              <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="tu-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="tu-email" className="mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          </div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })} disabled={!sel}>
              <SelectTrigger className="mt-1" data-testid="tu-role"><SelectValue placeholder={sel ? "Select role" : "Choose a tenant first"} /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="tu-save" className="bg-azx-founder hover:bg-amber-700">Invite user</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const isPlatform = !!user.platform_role;
  const [f, setF] = useState({ name: user.name, email: user.email, role: user.display_role });
  const roles = isPlatform ? ["platform_admin", "founder"] : ["tenant_admin", ...M1_INTERNAL];
  const save = async () => {
    try { await api.patch(`/platform/users/${user.id}`, f); toast.success("User updated"); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle>Edit user</DialogTitle><DialogDescription>Update name, email or role.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="eu-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="eu-email" className="mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className="mt-1" data-testid="eu-role"><SelectValue /></SelectTrigger>
              <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="eu-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Plans ---------------- */
function Plans() {
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => api.get("/platform/plans").then((r) => setPlans(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const archive = async (p) => { try { await api.post(`/platform/plans/${p.id}/archive`); toast.success("Archived"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (p) => { try { await api.delete(`/platform/plans/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Header title="Subscription Plans" sub="One plan belongs to exactly one module."
        action={<Button className="bg-azx-founder hover:bg-amber-700" data-testid="new-plan-btn" onClick={() => setModal({ type: "new" })}><Plus className="mr-2 h-4 w-4" /> New plan</Button>} />
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {plans.map((p) => (
          <div key={p.id} data-testid={`plan-card-${p.id}`} className={`rounded-lg border p-5 ${p.status === "archived" ? "border-slate-200 bg-slate-50 dark:bg-slate-900/40 opacity-70" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline" style={{ color: MOD_COLOR[p.module] }}>{MOD_LABEL[p.module]}</div>
                <div className="mt-1 font-heading text-lg font-bold flex items-center gap-2">{p.name}{p.status === "archived" && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">ARCHIVED</span>}</div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`plan-actions-${p.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem data-testid={`plan-edit-${p.id}`} onClick={() => setModal({ type: "edit", plan: p })}><Pencil className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                  {p.status !== "archived" && <DropdownMenuItem data-testid={`plan-archive-${p.id}`} onClick={() => archive(p)}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>}
                  <DropdownMenuItem className="text-red-600" data-testid={`plan-delete-${p.id}`} onClick={() => del(p)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="mt-3 text-2xl font-extrabold">${p.priceMonthly}<span className="text-sm font-normal text-slate-400">/mo</span> <span className="text-sm text-slate-400">· ${p.priceAnnual}/yr</span></div>
            <div className="mt-1 text-xs text-slate-500">{p.seatCap} seats · {p.secondaryCap} {p.module === "module_1" ? "engagements" : "tables"} · {p.subscriber_count} subscribers</div>
            <ul className="mt-3 space-y-1.5">
              {(p.features || []).map((f, i) => <li key={`${p.id}-${i}`} className="flex gap-2 text-sm text-slate-600 dark:text-slate-300"><CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: MOD_COLOR[p.module] }} />{f}</li>)}
            </ul>
          </div>
        ))}
      </div>
      {(modal?.type === "new" || modal?.type === "edit") && <PlanModal plan={modal.plan} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}

function PlanModal({ plan, onClose, onDone }) {
  const editing = !!plan;
  const [f, setF] = useState({
    name: plan?.name || "", module: "module_1", priceMonthly: plan?.priceMonthly ?? 0,
    priceAnnual: plan?.priceAnnual ?? 0, seatCap: plan?.seatCap ?? 10, secondaryCap: plan?.secondaryCap ?? 10,
    features: (plan?.features || []).join("\n"), status: plan?.status || "active",
  });
  const save = async () => {
    if (!f.name) return toast.error("Plan name required");
    const payload = { ...f, priceMonthly: Number(f.priceMonthly), priceAnnual: Number(f.priceAnnual), seatCap: Number(f.seatCap), secondaryCap: Number(f.secondaryCap), features: f.features.split("\n").map((x) => x.trim()).filter(Boolean) };
    try {
      if (editing) await api.patch(`/platform/plans/${plan.id}`, payload); else await api.post("/platform/plans", payload);
      toast.success(editing ? "Plan updated" : "Plan created"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit plan" : "New plan"}</DialogTitle><DialogDescription>A plan belongs to exactly one module.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="plan-name" className="mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Price / month ($)</Label><Input type="number" data-testid="plan-monthly" className="mt-1" value={f.priceMonthly} onChange={(e) => setF({ ...f, priceMonthly: e.target.value })} /></div>
            <div><Label>Price / year ($)</Label><Input type="number" data-testid="plan-annual" className="mt-1" value={f.priceAnnual} onChange={(e) => setF({ ...f, priceAnnual: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Seat cap</Label><Input type="number" data-testid="plan-seats" className="mt-1" value={f.seatCap} onChange={(e) => setF({ ...f, seatCap: e.target.value })} /></div>
            <div><Label>Engagement cap</Label><Input type="number" data-testid="plan-secondary" className="mt-1" value={f.secondaryCap} onChange={(e) => setF({ ...f, secondaryCap: e.target.value })} /></div>
          </div>
          <div><Label>Features (one per line)</Label><Textarea data-testid="plan-features" className="mt-1 min-h-[120px]" value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} /></div>
          <div className="flex items-center gap-2"><Checkbox checked={f.status === "active"} onCheckedChange={(v) => setF({ ...f, status: v ? "active" : "archived" })} data-testid="plan-active" /><span className="text-sm">Active (offered to new tenants)</span></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="plan-save" className="bg-azx-founder hover:bg-amber-700">{editing ? "Save" : "Create plan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Plan Requests ---------------- */
function PlanRequests() {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => api.get("/platform/plan-requests").then((r) => setReqs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (r, action) => {
    try { await api.post(`/platform/plan-requests/${r.id}/${action}`); toast.success(`Request ${action}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <Header title="Plan Requests" sub="Tenant-submitted plan changes awaiting a decision." />
      {reqs.length === 0 ? <Empty icon={GitPullRequest} title="No plan requests" /> : (
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3 text-left">Tenant</th><th className="px-4 py-3 text-left">Requested Plan</th><th className="px-4 py-3 text-left">Cycle</th><th className="px-4 py-3 text-left">Notes</th><th className="px-4 py-3 text-left">Requested</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-3 font-medium">{r.tenant_name}</td>
                  <td className="px-4 py-3">{r.plan_name}{!r.plan_valid && <span className="ml-1 text-[10px] font-bold text-red-500">INVALID</span>}</td>
                  <td className="px-4 py-3 capitalize">{r.billing_cycle}</td>
                  <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate">{r.notes || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{new Date(r.requested_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.status === "pending" && <>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 mr-1" data-testid={`approve-${r.id}`} disabled={!r.plan_valid} onClick={() => act(r, "approve")}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Approve</Button>
                      <Button size="sm" variant="outline" className="text-red-600" data-testid={`reject-${r.id}`} onClick={() => act(r, "reject")}>Reject</Button>
                    </>}
                    {r.status === "approved" && <Button size="sm" variant="outline" data-testid={`reapprove-${r.id}`} disabled={!r.plan_valid} onClick={() => act(r, "reapprove")}><RefreshCw className="mr-1 h-3.5 w-3.5" /> Re-approve</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Support Access + Audit (unchanged behavior) ---------------- */
function SupportAccess() {
  const { loadMe } = useAuth();
  const nav = useNavigate();
  const [grants, setGrants] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [modal, setModal] = useState(false);
  const [tid, setTid] = useState(""); const [reason, setReason] = useState("");
  const load = useCallback(() => { api.get("/platform/support-grants").then((r) => setGrants(r.data)); api.get("/platform/tenants").then((r) => setTenants(r.data.filter((t) => !t.is_platform_tenant))); }, []);
  useEffect(() => { load(); }, [load]);
  const elevate = async () => { if (!tid || !reason) return toast.error("Select a tenant and give a reason"); try { await api.post("/platform/support-grants", { tenant_id: tid, reason }); toast.success("Elevation granted (logged)"); setModal(false); setReason(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const enter = async (g) => { localStorage.setItem("azx_act_as", g.tenant_id); await loadMe(); toast.success(`Entering ${g.tenant_name}`); nav("/"); };
  const revoke = async (g) => { try { await api.post(`/platform/support-grants/${g.id}/revoke`); if (localStorage.getItem("azx_act_as") === g.tenant_id) localStorage.removeItem("azx_act_as"); toast.success("Revoked"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Header title="Support Access" sub="Every elevation into a tenant workspace is explicit, temporary and logged."
        action={<Button className="bg-azx-founder hover:bg-amber-700" data-testid="grant-elevation-btn" onClick={() => setModal(true)}><ShieldCheck className="mr-2 h-4 w-4" /> Grant elevation</Button>} />
      {grants.length === 0 ? <Empty icon={ShieldCheck} title="No elevations yet" /> : (
        <div className="space-y-2">
          {grants.map((g) => (
            <div key={g.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
              <div><div className="font-semibold">{g.tenant_name}</div><div className="text-xs text-slate-500">{g.reason}</div><div className="text-[11px] font-mono text-slate-400">{new Date(g.created_at).toLocaleString()} · {g.active ? "ACTIVE" : "revoked"}</div></div>
              {g.active && <div className="flex gap-2"><Button size="sm" variant="outline" data-testid={`enter-${g.id}`} onClick={() => enter(g)}><LogIn className="mr-1.5 h-3.5 w-3.5" /> Enter</Button><Button size="sm" variant="ghost" className="text-red-600" onClick={() => revoke(g)}><Power className="mr-1.5 h-3.5 w-3.5" /> Revoke</Button></div>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent><DialogHeader><DialogTitle>Grant support elevation</DialogTitle><DialogDescription>Read-only access into the tenant, fully logged.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Tenant</Label><Select value={tid} onValueChange={setTid}><SelectTrigger className="mt-1.5" data-testid="elevation-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger><SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Reason (logged)</Label><Textarea data-testid="elevation-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={elevate} data-testid="elevation-confirm">Grant & log</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AuditLog() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/platform/audit-log").then((r) => setLogs(r.data)); }, []);
  return (
    <div>
      <Header title="Founder Action Log" sub="Technical trail of platform-level actions, separate from tenant activity." />
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3 text-left">When</th><th className="px-4 py-3 text-left">Action</th><th className="px-4 py-3 text-left">Resource</th><th className="px-4 py-3 text-left">Reason</th></tr></thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100 dark:border-slate-800"><td className="px-4 py-2.5 font-mono text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</td><td className="px-4 py-2.5 font-medium">{l.action}</td><td className="px-4 py-2.5 text-slate-500">{l.resource}</td><td className="px-4 py-2.5 text-slate-500">{l.reason}</td></tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No entries</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ---------------- Storage Health ---------------- */
function bytesFmt(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, x = n;
  while (x >= 1024 && i < u.length - 1) { x /= 1024; i++; }
  return `${x.toFixed(x < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function gbFmt(n) {
  return `${((n || 0) / 1073741824).toFixed(2)} GB`;
}
function gbNum(n) {
  return ((n || 0) / 1073741824).toFixed(2);
}

function StorageHealth() {
  const [d, setD] = useState(null);
  const [purging, setPurging] = useState(false);
  const load = useCallback(() => api.get("/platform/storage/health").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const purgeNow = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/platform/storage/purge-now");
      toast.success(`Cleanup complete · ${data.purged_requests} request(s), ${data.purged_attachments} attachment(s)${data.s3_failures ? `, ${data.s3_failures} failed` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setPurging(false); }
  };
  if (!d) return <Loading />;
  const at = d.attachments, rb = d.recycle_bin, fails = d.delete_failures || [];
  return (
    <div data-testid="storage-health">
      <Header title="Storage Health" sub="Attachment storage, recycle-bin retention and S3 cleanup visibility."
        action={<Button data-testid="purge-now-btn" disabled={purging} onClick={purgeNow} className="bg-azx-founder hover:bg-amber-700">
          {purging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />} Run cleanup now
        </Button>} />

      {fails.length > 0 && (
        <div data-testid="storage-failure-banner" className="mb-6 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-red-800 dark:text-red-300">{fails.length} S3 object(s) failed to delete</div>
            <p className="text-sm text-red-700/80 dark:text-red-300/70 mt-0.5">Their records are kept in the recycle bin and retried on the next cleanup. Fix the cause (usually IAM permissions), then run cleanup.</p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Objects Stored" value={at.active} icon={HardDrive} accent="#2563EB" sub="active attachments" />
        <StatCard label="Storage Used" value={bytesFmt(at.bytes)} icon={Database} accent="#059669" />
        <StatCard label="In Recycle Bin" value={rb.deleted_requests + rb.deleted_attachments} icon={Trash2} accent="#D97706" sub={`${rb.deleted_requests_due + rb.deleted_attachments_due} due for purge`} />
        <StatCard label="Delete Failures" value={fails.length} icon={AlertTriangle} accent={fails.length ? "#DC2626" : "#94A3B8"} sub="awaiting retry" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-5">
          <div className="overline text-azx-founder mb-3">S3 Bucket</div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-4"><dt className="text-slate-500">Bucket</dt><dd className="font-mono text-xs text-right break-all">{d.bucket}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Region</dt><dd className="font-mono text-xs">{d.region}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">On S3</dt><dd>{at.s3} object(s)</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Legacy (DB) </dt><dd>{at.legacy} object(s)</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Pending uploads</dt><dd>{at.pending}</dd></div>
          </dl>
        </div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-5">
          <div className="overline text-azx-founder mb-3">Recycle Bin · {d.retention_days}-day retention</div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-slate-500">Deleted requests</dt><dd>{rb.deleted_requests} <span className="text-slate-400">({rb.deleted_requests_due} due)</span></dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">Deleted attachments</dt><dd>{rb.deleted_attachments} <span className="text-slate-400">({rb.deleted_attachments_due} due)</span></dd></div>
          </dl>
          <p className="text-[11px] text-slate-400 mt-3">Items older than {d.retention_days} days are permanently purged on the daily cleanup (or via “Run cleanup now”).</p>
        </div>
      </div>

      <div className="mt-6">
        <div className="overline text-azx-founder mb-2">Recent cleanup runs</div>
        <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3 text-left">When</th><th className="px-4 py-3 text-left">Trigger</th><th className="px-4 py-3 text-left">Requests</th><th className="px-4 py-3 text-left">Attachments</th><th className="px-4 py-3 text-left">S3 failures</th></tr>
            </thead>
            <tbody>
              {d.purge_runs.map((r) => (
                <tr key={r.id} data-testid={`purge-run-${r.id}`} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{new Date(r.ran_at).toLocaleString()}</td>
                  <td className="px-4 py-2.5 capitalize">{r.trigger}</td>
                  <td className="px-4 py-2.5">{r.purged_requests}</td>
                  <td className="px-4 py-2.5">{r.purged_attachments}</td>
                  <td className={`px-4 py-2.5 font-semibold ${r.s3_failures ? "text-red-600" : "text-slate-400"}`}>{r.s3_failures}</td>
                </tr>
              ))}
              {d.purge_runs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No cleanup runs yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {fails.length > 0 && (
        <div className="mt-6">
          <div className="overline text-red-500 mb-2">Unresolved S3 delete failures</div>
          <div className="rounded-lg border border-red-200 dark:border-red-900 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-red-50 dark:bg-red-950/30 text-xs uppercase tracking-wider text-red-600">
                <tr><th className="px-4 py-3 text-left">Object key</th><th className="px-4 py-3 text-left">Kind</th><th className="px-4 py-3 text-left">Last tried</th><th className="px-4 py-3 text-left">Error</th></tr>
              </thead>
              <tbody>
                {fails.map((f) => (
                  <tr key={f.key} data-testid={`delete-failure-row`} className="border-t border-red-100 dark:border-red-900/50">
                    <td className="px-4 py-2.5 font-mono text-[11px] break-all max-w-[280px]">{f.key}</td>
                    <td className="px-4 py-2.5 text-slate-500">{f.kind}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{f.last_seen ? new Date(f.last_seen).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2.5 text-red-600 text-xs max-w-[320px] truncate" title={f.error}>{f.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <StorageByTenant />
      <DownloadsByTenant />
      <StorageReconcile />
    </div>
  );
}

/* ---------------- Storage by Tenant ---------------- */
function _csv(rows) {
  return rows.map((r) => r.map((c) => {
    const s = String(c ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
}
function downloadCsv(filename, rows) {
  const blob = new Blob([_csv(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function StorageByTenant() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  useEffect(() => { api.get("/platform/storage/by-tenant").then((r) => setRows(r.data.tenants)).catch(() => {}); }, []);
  const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const exportCsv = (withEngs) => {
    const head = ["Tenant", "Active (GB)", "Recycle bin (GB)", "Total billable (GB)", "Engagements"];
    const out = [withEngs ? [...head.slice(0, 1), "Engagement", ...head.slice(1, 4)] : head];
    (rows || []).forEach((t) => {
      out.push(withEngs ? [t.tenant, "", gbNum(t.active), gbNum(t.recycle), gbNum(t.total)]
                        : [t.tenant, gbNum(t.active), gbNum(t.recycle), gbNum(t.total), t.engagements_count]);
      if (withEngs) (t.engagements || []).forEach((e) => out.push([t.tenant, e.name, gbNum(e.active), gbNum(e.recycle), gbNum(e.total)]));
    });
    downloadCsv(`azexpan-storage-by-tenant${withEngs ? "-with-engagements" : ""}.csv`, out);
  };
  if (!rows) return null;
  return (
    <div className="mt-8" data-testid="storage-by-tenant">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline text-azx-founder">Storage by Tenant</div>
          <p className="text-xs text-slate-400 mt-0.5">Billable = active + recycle bin (soft-deleted files still occupy S3 until purged). Expand a firm to see its engagements.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="storage-tenant-csv" variant="outline" size="sm" onClick={() => exportCsv(false)}><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
          <Button data-testid="storage-tenant-csv-engagements" variant="outline" size="sm" onClick={() => exportCsv(true)}><Download className="mr-1.5 h-3.5 w-3.5" />CSV + engagements</Button>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Firm</th>
              <th className="px-4 py-3 text-right">Active</th>
              <th className="px-4 py-3 text-right">Recycle bin</th>
              <th className="px-4 py-3 text-right">Total billable</th>
              <th className="px-4 py-3 text-right">Engagements</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <React.Fragment key={t.tenant_id}>
                <tr data-testid={`storage-tenant-row-${t.tenant_id}`} onClick={() => toggle(t.tenant_id)}
                    className="border-t border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <td className="px-4 py-2.5 font-medium flex items-center gap-1.5">
                    {(t.engagements || []).length > 0
                      ? (open.has(t.tenant_id) ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />)
                      : <span className="w-4" />}
                    {t.tenant}
                  </td>
                  <td className="px-4 py-2.5 text-right">{gbFmt(t.active)}</td>
                  <td className="px-4 py-2.5 text-right text-amber-600">{gbFmt(t.recycle)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold">{gbFmt(t.total)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-500">{t.engagements_count}</td>
                </tr>
                {open.has(t.tenant_id) && (t.engagements || []).map((e) => (
                  <tr key={e.engagement_id} data-testid={`storage-eng-row-${e.engagement_id}`} className="bg-slate-50/60 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800/50">
                    <td className="px-4 py-2 pl-11 text-slate-600 dark:text-slate-300">{e.name}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{gbFmt(e.active)}</td>
                    <td className="px-4 py-2 text-right text-amber-600/80">{gbFmt(e.recycle)}</td>
                    <td className="px-4 py-2 text-right">{gbFmt(e.total)}</td>
                    <td className="px-4 py-2" />
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No tenants yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DownloadsByTenant() {
  const [period, setPeriod] = useState("month");
  const [rows, setRows] = useState(null);
  useEffect(() => { setRows(null); api.get(`/platform/storage/downloads?period=${period}`).then((r) => setRows(r.data.tenants)).catch(() => {}); }, [period]);
  const exportCsv = () => {
    const out = [["Tenant", "GB downloaded", "Download count"]];
    (rows || []).forEach((t) => out.push([t.tenant, gbNum(t.bytes), t.count]));
    downloadCsv(`azexpan-downloads-${period}.csv`, out);
  };
  return (
    <div className="mt-8" data-testid="downloads-by-tenant">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline text-azx-founder">Downloads by Tenant</div>
          <p className="text-xs text-slate-400 mt-0.5">Approximate egress: each served file / presigned link is counted as one full transfer — not an AWS-verified byte count.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-xs">
            <button data-testid="downloads-period-month" onClick={() => setPeriod("month")} className={`px-3 py-1.5 ${period === "month" ? "bg-azx-founder text-white" : "text-slate-500"}`}>This month</button>
            <button data-testid="downloads-period-all" onClick={() => setPeriod("all")} className={`px-3 py-1.5 ${period === "all" ? "bg-azx-founder text-white" : "text-slate-500"}`}>All-time</button>
          </div>
          <Button data-testid="downloads-csv" variant="outline" size="sm" onClick={exportCsv}><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Firm</th>
              <th className="px-4 py-3 text-right">Downloaded</th>
              <th className="px-4 py-3 text-right">Downloads</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((t) => (
              <tr key={t.tenant_id} data-testid={`downloads-row-${t.tenant_id}`} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2.5 font-medium">{t.tenant}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{gbFmt(t.bytes)}</td>
                <td className="px-4 py-2.5 text-right text-slate-500">{t.count}</td>
              </tr>
            ))}
            {rows && rows.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-slate-400">No downloads recorded {period === "month" ? "this month" : "yet"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Reconcile against real AWS S3 data (V2) ---------------- */
function deltaCell(n) {
  if (!n) return <span className="text-slate-400">—</span>;
  const pos = n > 0;
  return <span className={pos ? "text-emerald-600" : "text-red-600"}>{pos ? "+" : "−"}{gbFmt(Math.abs(n))}</span>;
}

function ReconTable({ title, note, rows, actualLabel, trackedLabel }) {
  return (
    <div className="mt-4">
      <div className="overline text-slate-500 mb-1">{title}</div>
      {note && <p className="text-xs text-slate-400 mb-2">{note}</p>}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Firm</th>
              <th className="px-4 py-3 text-right">{actualLabel}</th>
              <th className="px-4 py-3 text-right">{trackedLabel}</th>
              <th className="px-4 py-3 text-right">Delta (actual − tracked)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.tenant_id} data-testid={`recon-row-${r.tenant_id}`} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2.5 font-medium">{r.tenant}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{gbFmt(r.actual_bytes)}</td>
                <td className="px-4 py-2.5 text-right text-slate-500">{gbFmt(r.tracked_bytes)}</td>
                <td className="px-4 py-2.5 text-right">{deltaCell(r.delta_bytes)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No data</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StorageReconcile() {
  const [d, setD] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/platform/storage/reconcile").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/platform/storage/reconcile");
        setD(data);
        if (data.status === "done" || data.status === "error") setRunning(false);
      } catch (e) { console.error("recon poll", e); }
    }, 3000);
    return () => clearInterval(t);
  }, [running]);
  const start = async () => {
    try {
      const { data } = await api.post("/platform/storage/reconcile");
      setRunning(true);
      if (data.status === "running") toast.info("A reconciliation is already running");
      else toast.success("Reconciliation started — this can take a while for large buckets");
    } catch (e) { toast.error(apiError(e)); }
  };
  const busy = running || d?.status === "running";
  return (
    <div className="mt-8" data-testid="storage-reconcile">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline text-azx-founder">Reconcile against AWS (V2)</div>
          <p className="text-xs text-slate-400 mt-0.5">
            Compares our tracked figures with real S3 data — actual object sizes for storage, and S3 access logs for egress.
            {d?.ran_at && <> Last run: {new Date(d.ran_at).toLocaleString()}.</>}
          </p>
        </div>
        <Button data-testid="reconcile-btn" disabled={busy} onClick={start} className="bg-azx-founder hover:bg-amber-700">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          {busy ? "Reconciling…" : "Reconcile against AWS"}
        </Button>
      </div>

      {(!d || d.status === "none") && <p className="text-sm text-slate-400 py-4">No reconciliation has run yet.</p>}
      {d?.status === "error" && <p className="text-sm text-red-600 py-2">Last run failed: {d.error}</p>}

      {d?.storage && (
        <ReconTable
          title={`Storage — actual S3 vs tracked (${d.storage.total_objects} objects scanned)`}
          note="A negative delta usually means legacy in-database attachments not stored on S3; a positive delta can indicate orphaned objects to clean up."
          rows={d.storage.rows} actualLabel="Actual on S3" trackedLabel="Tracked (DB)" />
      )}

      {d?.egress && (
        d.egress.configured ? (
          <ReconTable
            title={`Egress this month — actual S3 logs vs tracked (${d.egress.log_files} log files, ${d.egress.log_lines} GET lines)`}
            note="Actual = bytes_sent from S3 server access logs; tracked = our download_events for the same period."
            rows={d.egress.rows} actualLabel="Actual (S3 logs)" trackedLabel="Tracked (app)" />
        ) : (
          <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-800 p-4 text-sm text-slate-500" data-testid="egress-not-configured">
            <span className="font-medium">Egress reconciliation not configured.</span> Enable S3 Server Access Logging on the attachments bucket and set <code className="text-xs">S3_LOG_BUCKET</code> (and read permission on that log bucket) to compare real egress against tracked downloads.
          </div>
        )
      )}
    </div>
  );
}

